import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimePeriodRecurrence } from '@acceligentllc/shared/enums/timePeriodRecurrence';

import type { RootState } from 'af-reducers';

import type { UserTypeFilterOption } from 'ab-enums/userTypeFilter.enum';

import { SearchInput } from '@acceligentllc/storybook';
import { Button } from '@acceligentllc/storybook';

import Dropdown from 'af-components/Controls/Dropdown';
import CustomModal from 'af-components/CustomModal';
import DateFilterNew from 'af-components/Table/Filters/DateFilter';
import type { MainFilterOption } from 'af-components/Table/Filters/MainFilter';

import type { FieldReportBulkFilters } from '.';
import { MAIN_FILTER_OPTION_IDS, MAIN_FILTER_OPTIONS } from '.';
import BulkModalAdditionalFilters from './Filters/BulkModalAdditionalFilters';
import type { CrewTypeFilterOption } from './Filters/CrewTypeFilter';
import type { JobFilterOption } from './Filters/JobFilter';
import MainJobFilter from './Filters/MainJobFilter';
import type { ProjectManagerFilterOption } from './Filters/ProjectManagerFilter';
import type { ReviewStatusFilterOption } from './Filters/ReviewStatusFilter';
import type { SuperintendentFilterOption } from './Filters/SuperintendentFilter';
import type { WorkSummaryStatusFilterOption } from './Filters/WorkSummaryStatusFilter';
import styles from './styles.module.scss';

const AVAILABLE_DATE_PERIODS = [
	{ id: TimePeriodRecurrence.DAILY, label: 'Daily' },
	{ id: TimePeriodRecurrence.WEEKLY, label: 'Weekly' },
	{ id: TimePeriodRecurrence.MONTHLY, label: 'Monthly' },
	{ id: TimePeriodRecurrence.FISCAL_YEAR, label: 'Fiscal Year' },
	{ id: TimePeriodRecurrence.LAST_6_MONTHS, label: 'Last 6 Months' },
	{ id: TimePeriodRecurrence.YEAR_TO_DATE, label: 'Year to Date' },
];

interface OwnProps {
	isOpen: boolean;
	onClose: () => void;
	applyFilters: (filters: FieldReportBulkFilters) => void;
	startDate: Date;
	endDate: Date;
	period: TimePeriodRecurrence;
	fiscalYearStartMonth: Nullable<number>;
	defaultValues: FieldReportBulkFilters;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const BulkFilteringModal: React.FC<Props> = (props) => {

	const {
		isOpen,
		onClose,
		applyFilters,
		defaultValues,
		fieldReportFullViewFilterContext,
		fiscalYearStartMonth,
		companyData,
	} = props;

	const [selectedMainFilterId, setSelectedMainFilterId] = React.useState<number>(defaultValues.mainFilterId);

	const [jobFilter, setJobFilter] = React.useState<Nullable<number>>(defaultValues.jobFilter ?? null);
	const [selectedJobFilters, setSelectedJobFilters] = React.useState<JobFilterOption[]>(defaultValues.jobFilters);
	const [
		selectedProjectManagerFilters,
		setSelectedProjectManagerFilters,
	] = React.useState<ProjectManagerFilterOption[]>(defaultValues.projectManagerFilters);
	const [selectedSuperintendentFilters, setSelectedSuperintendentFilters] = React.useState<SuperintendentFilterOption[]>(defaultValues.superintendentFilters);
	const [assignmentFilter, setAssignmentFilter] = React.useState<Nullable<UserTypeFilterOption>>(defaultValues.assignmentFilter ?? null);
	const [selectedCrewTypeFilters, setSelectedCrewTypeFilters] = React.useState<CrewTypeFilterOption[]>(defaultValues.crewTypeFilters);
	const [selectedReviewStatusFilters, setSelectedReviewStatusFilters] = React.useState<ReviewStatusFilterOption[]>(defaultValues.reviewStatusFilters);
	const [selectedWorkSummaryStatusFilters, setSelectedWorkSummaryStatusFilters] =
		React.useState<WorkSummaryStatusFilterOption[]>(defaultValues.workSummaryStatusFilters);
	const [requiresMyAttention, setRequiresMyAttention] = React.useState(defaultValues.requiresMyAttention);
	const [searchText, setSearchText] = React.useState(defaultValues.searchText);
	const [excludeCancelled, setExcludeCancelled] = React.useState(defaultValues.excludeCancelled);

	const [period, setPeriod] = React.useState(defaultValues.period ?? TimePeriodRecurrence.DAILY);
	const [dateRange, setDateRange] = React.useState<{ startDate: Date; endDate: Date; }>(defaultValues.dateRange ?? {
		startDate: new Date(),
		endDate: new Date(),
	});
	const { startDate, endDate } = dateRange;

	React.useEffect(() => {
		if (isOpen) {
			setSelectedMainFilterId(defaultValues.mainFilterId);
			setJobFilter(defaultValues.jobFilter ?? null);
			setSelectedJobFilters(defaultValues.jobFilters);
			setSelectedProjectManagerFilters(defaultValues.projectManagerFilters);
			setSelectedSuperintendentFilters(defaultValues.superintendentFilters);
			setAssignmentFilter(defaultValues.assignmentFilter ?? null);
			setSelectedCrewTypeFilters(defaultValues.crewTypeFilters);
			setSelectedReviewStatusFilters(defaultValues.reviewStatusFilters);
			setSelectedWorkSummaryStatusFilters(defaultValues.workSummaryStatusFilters);
			setRequiresMyAttention(defaultValues.requiresMyAttention);
			setSearchText(defaultValues.searchText);
			setDateRange(defaultValues.dateRange ?? {
				startDate: new Date(),
				endDate: new Date(),
			});
			setPeriod(defaultValues.period ?? TimePeriodRecurrence.DAILY);
			setExcludeCancelled(defaultValues.excludeCancelled);
		}
		// This should be triggered only when opening the modal
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const reset = React.useCallback(() => {
		setSelectedJobFilters([]);
		setSelectedProjectManagerFilters([]);
		setSelectedSuperintendentFilters([]);
		setAssignmentFilter(null);
		setSelectedCrewTypeFilters([]);
		setSelectedReviewStatusFilters([]);
		setSelectedWorkSummaryStatusFilters([]);
		setRequiresMyAttention(false);
		setSearchText('');
		setExcludeCancelled(false);
	}, []);

	const closeModal = React.useCallback(() => {
		reset();
		onClose();
	}, [onClose, reset]);

	const changeJobFilter = React.useCallback((jobId: number) => {
		setJobFilter(jobId);
	}, [setJobFilter]);

	const changePeriod = React.useCallback((newPeriod: TimePeriodRecurrence, selected: Date) => {
		let from: Date = selected, to: Date = selected;

		switch (newPeriod) {
			case TimePeriodRecurrence.MONTHLY:
				from = TimeUtils.positionDate(from, 'start', 'month');
				to = TimeUtils.positionDate(to, 'end', 'month');
				break;
			case TimePeriodRecurrence.WEEKLY:
				from = TimeUtils.positionDate(from, 'start', 'week');
				to = TimeUtils.positionDate(to, 'end', 'week');
				break;
			case TimePeriodRecurrence.YEAR_TO_DATE:
				from = TimeUtils.positionDate(new Date(), 'start', 'year');
				to = new Date();
				break;
			case TimePeriodRecurrence.LAST_6_MONTHS:
				const _endDate = new Date();
				const _startDate = new Date();
				const day = _endDate.getDate();
				let month = _startDate.getMonth() - 6;
				let year = _startDate.getFullYear();

				if (month < 0) {
					month += 12;
					year -= 1;
				}

				const lastDayOfTarget = new Date(year, month + 1, 0).getDate(); // if we put 0 as date argument, we get the last day index of previous month
				const newDay = Math.min(day, lastDayOfTarget);

				_startDate.setFullYear(year, month, newDay);

				from = _startDate;
				to = _endDate;
				break;
			case TimePeriodRecurrence.FISCAL_YEAR:
				const fiscalMonth = companyData.fiscalYearStartMonth!;
				const selMonth = selected.getMonth() + 1;
				let fyYear = selected.getFullYear();

				if (selMonth < fiscalMonth) {
					fyYear -= 1;
				}

				const fyStart = new Date(fyYear, fiscalMonth, 0);
				const fyEnd = new Date(fyYear + 1, fiscalMonth - 1, 0);
				from = TimeUtils.positionDate(fyStart, 'start', 'month');
				to = TimeUtils.positionDate(fyEnd, 'end', 'day');
				break;
			case TimePeriodRecurrence.DAILY:
			case TimePeriodRecurrence.CUSTOM:
			default:
				break;
		}

		setPeriod(newPeriod);
		setDateRange({ startDate: from, endDate: to });
	}, [companyData.fiscalYearStartMonth]);

	const filterByDate = React.useCallback((from: Date, to: Date) => {
		setDateRange({ startDate: from, endDate: to });
	}, []);

	const changeJobFilters = React.useCallback((jobOptions: JobFilterOption[]) => {
		setSelectedJobFilters(jobOptions);
	}, []);

	const changeProjectManagerFilters = React.useCallback((projectManagerOptions: ProjectManagerFilterOption[]) => {
		setSelectedProjectManagerFilters(projectManagerOptions);
	}, []);

	const changeSuperintendentFilters = React.useCallback((superintendentOptions: SuperintendentFilterOption[]) => {
		setSelectedSuperintendentFilters(superintendentOptions);
	}, []);

	const changeAssignmentsFilter = React.useCallback((filter: UserTypeFilterOption) => {
		setAssignmentFilter(filter);
	}, [setAssignmentFilter]);

	const changeCrewTypeFilters = React.useCallback((crewTypeOptions: CrewTypeFilterOption[]) => {
		setSelectedCrewTypeFilters(crewTypeOptions);
	}, []);

	const changeReviewStatusFilters = React.useCallback((statusOptions: ReviewStatusFilterOption[]) => {
		setSelectedReviewStatusFilters(statusOptions);
	}, []);

	const changeWorkSummaryStatusFilters = React.useCallback((statusOptions: WorkSummaryStatusFilterOption[]) => {
		setSelectedWorkSummaryStatusFilters(statusOptions);
	}, []);

	const changeRequiresMyAttention = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setRequiresMyAttention(event.target.checked);
	}, []);

	const changeExcludeCancelled = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setExcludeCancelled(event.target.checked);
	}, []);

	const changeMainFilter = React.useCallback((option: MainFilterOption) => {
		setSelectedMainFilterId(option.id);
	}, [setSelectedMainFilterId]);

	const changeSearchText = React.useCallback((e: React.ChangeEvent<HTMLInputElement> | string) => {
		const currFilterText = typeof e === 'string' ? e : e.target.value;
		setSearchText(currFilterText);
	}, [setSearchText]);

	const submit = React.useCallback(() => {
		const filters: FieldReportBulkFilters = {
			assignmentFilter,
			crewTypeFilters: selectedCrewTypeFilters,
			jobFilters: selectedJobFilters,
			mainFilterId: selectedMainFilterId,
			projectManagerFilters: selectedProjectManagerFilters,
			reviewStatusFilters: selectedReviewStatusFilters,
			workSummaryStatusFilters: selectedWorkSummaryStatusFilters,
			superintendentFilters: selectedSuperintendentFilters,
			dateRange,
			jobFilter,
			period,
			requiresMyAttention,
			searchText,
			excludeCancelled,
		};
		applyFilters(filters);
		closeModal();
	}, [
		applyFilters,
		assignmentFilter,
		closeModal,
		dateRange,
		jobFilter,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedMainFilterId,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		period,
		requiresMyAttention,
		searchText,
		selectedReviewStatusFilters,
		selectedWorkSummaryStatusFilters,
		excludeCancelled,
	]);

	const datePeriodOptions = React.useMemo(() => {
		if (!!fiscalYearStartMonth) {
			return AVAILABLE_DATE_PERIODS;
		}
		return AVAILABLE_DATE_PERIODS.filter(({ id }) => id !== TimePeriodRecurrence.FISCAL_YEAR);
	}, [fiscalYearStartMonth]);

	const renderAdditionalFilters = React.useCallback(() => {
		return (
			<BulkModalAdditionalFilters
				assignmentFilter={assignmentFilter}
				changeAssignmentsFilter={changeAssignmentsFilter}
				changeCrewTypeFilters={changeCrewTypeFilters}
				changeExcludeCancelled={changeExcludeCancelled}
				changeJobFilters={changeJobFilters}
				changeProjectManagerFilters={changeProjectManagerFilters}
				changeRequiresMyAttention={changeRequiresMyAttention}
				changeReviewStatusFilters={changeReviewStatusFilters}
				changeSuperintendentFilters={changeSuperintendentFilters}
				changeWorkSummaryStatusFilters={changeWorkSummaryStatusFilters}
				excludeCancelled={excludeCancelled}
				fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
				requiresMyAttention={requiresMyAttention}
				selectedCrewTypeFilters={selectedCrewTypeFilters}
				selectedJobFilters={selectedJobFilters}
				selectedMainFilterId={selectedMainFilterId}
				selectedProjectManagerFilters={selectedProjectManagerFilters}
				selectedReviewStatusFilters={selectedReviewStatusFilters}
				selectedSuperintendentFilters={selectedSuperintendentFilters}
				selectedWorkSummaryStatusFilters={selectedWorkSummaryStatusFilters}
			/>);
	}, [
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		selectedMainFilterId,
		requiresMyAttention,
		assignmentFilter,
		changeRequiresMyAttention,
		changeWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		selectedReviewStatusFilters,
		fieldReportFullViewFilterContext,
		excludeCancelled,
		changeExcludeCancelled,
	]);

	const renderSelected = React.useCallback((option: MainFilterOption) => {
		return (
			<>
				{option.label}
			</>
		);
	}, []);

	const renderMainFilter = React.useCallback(() => {
		let filterName = '';
		let body = <div />;
		if (selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB) {
			filterName = 'Job';
			body = <div className={styles['bulk-modal__job-filter']}>
				<MainJobFilter
					defaultValue={jobFilter}
					fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
					forBulkModal={true}
					onChange={changeJobFilter}
				/>
			</div>;
		} else {
			filterName = 'Date';
			body =
				<div className={styles['bulk-modal__date-filter']}>
					<DateFilterNew
						changePeriod={changePeriod}
						endDate={endDate}
						onChange={filterByDate}
						options={datePeriodOptions}
						period={period}
						startDate={startDate}
					/>
				</div>;
		}
		return <>
			<div className={styles['bulk-modal__main-filter']} key="main-filter">
				<div>Filter By</div>
				<div className={styles['bulk-modal__main-filter__dropdown']}>
					<Dropdown
						defaultValue={MAIN_FILTER_OPTIONS.find(({ id }) => id === selectedMainFilterId)}
						fullWidth={true}
						onValueChange={changeMainFilter}
						options={MAIN_FILTER_OPTIONS}
						renderMenuItem={renderSelected}
						valueKey="id"
						withCaret={true}
					/>
				</div>
			</div>
			<div className={styles['bulk-modal__main-filter']} key={selectedMainFilterId}>
				<div>{filterName}</div>
				<div>{body}</div>
			</div>
		</>;
	}, [
		changeJobFilter,
		changeMainFilter,
		changePeriod,
		endDate,
		filterByDate,
		period,
		renderSelected,
		selectedMainFilterId,
		startDate,
		fieldReportFullViewFilterContext,
		datePeriodOptions,
		jobFilter,
	]);

	const renderSearchText = React.useCallback(() => {
		return <div className={styles['bulk-modal__filter']} key="searchQuery">
			<div>
				Search Query
			</div>
			<SearchInput
				name="searchQuery"
				onChange={changeSearchText}
				placeholder="Search"
				value={searchText}
			/>
		</div>;
	}, [
		changeSearchText,
		searchText,
	]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={isOpen}
			size="md"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Advanced Search Options"
			/>
			<CustomModal.Body>
				<div>
					{renderMainFilter()}
					{renderSearchText()}
					{renderAdditionalFilters()}
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Close"
					onClick={closeModal}
					style="secondary"
				/>
				<Button
					label="Reset"
					onClick={reset}
					style="secondary"
				/>
				<Button
					label="Apply"
					onClick={submit}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;

	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyData,
	};
}

const connector = connect(mapStateToProps, null);

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(BulkFilteringModal);
